/**
 * HideReasonModal — modal de confirmación para bulk-hide.
 * El usuario elige el motivo (out_of_scope, low_confidence, manual) y opcionalmente
 * deja una nota interna.
 *
 * Spec UX: DATA_HEALTH_PANEL_SPEC.md §5.1
 */
import React, { useState } from 'react';
import { EyeOff, X, Loader2 } from 'lucide-react';
import type { HiddenReason, DataHealthItem } from '../../../types/dataHealth';
import { HIDDEN_REASON_LABELS } from '../../../types/dataHealth';

interface Props {
  isOpen: boolean;
  selectedItems: DataHealthItem[];
  onClose: () => void;
  onConfirm: (reason: HiddenReason, note: string) => Promise<void>;
}

const REASON_OPTIONS: HiddenReason[] = [
  'hidden_out_of_scope',
  'hidden_low_confidence',
  'hidden_manual',
  'hidden_duplicate',
];

const HideReasonModal: React.FC<Props> = ({ isOpen, selectedItems, onClose, onConfirm }) => {
  const [reason, setReason] = useState<HiddenReason>('hidden_out_of_scope');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(reason, note);
      setNote('');
      onClose();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Error desconocido';
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const preview = selectedItems.slice(0, 5);
  const overflow = selectedItems.length - preview.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl border border-gray-200">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">
              Ocultar {selectedItems.length} producto{selectedItems.length === 1 ? '' : 's'}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Motivo</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as HiddenReason)}
              disabled={submitting}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {REASON_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {HIDDEN_REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Nota (opcional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              rows={2}
              placeholder="Contexto interno para el historial…"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs">
            <div className="font-medium text-gray-700 mb-1">Preview ({preview.length} de {selectedItems.length})</div>
            <ul className="space-y-0.5 text-gray-600">
              {preview.map((it, i) => (
                <li key={it.id_catalogo || i} className="truncate">
                  · {it.title || `id_catalogo ${it.id_catalogo}`}
                </li>
              ))}
              {overflow > 0 && <li className="text-gray-500 italic">+ {overflow} más…</li>}
            </ul>
          </div>

          <div className="text-[11px] text-gray-500 bg-indigo-50 border-l-2 border-indigo-300 rounded px-2 py-1.5">
            Los productos ocultos no aparecen en publicaciones ni estadísticas. La acción
            queda registrada en el historial y es reversible desde la tab Ocultos.
          </div>

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
            Ocultar {selectedItems.length}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HideReasonModal;
